import { authUtils } from "../../util/AuthUtils";
import { baseUrl } from "../../config/apiEndpoint";

/**
 * API Client for handling requests with token management.
 */
class APIClient {
  /**
   * Initializes APIClient with base URL.
   */
  constructor() {
    this.baseUrl = baseUrl;
  }

  /**
   * util to handle response.
   * @param {string} response
   * @return {object} response
   */
  async handleResponse(response) {
    const json = await response.json();

    return {
      success: json.success,
      message: json.message,
      data: json.data ?? null,
      error: json.error ?? null,
      meta: json.meta ?? null,
      raw: json // keep raw if needed
    };
  }

  /**
   * Main request method with automatic token handling.
   * @param {string} endpoint - API endpoint
   * @param {object} [options={}] - Request options
   * @param {boolean} requireAuth - is auth token required
   * @return {Promise<Response>} API response
   */
  async request(endpoint, options = {}, requireAuth = true) {
    let token = null;

    if (requireAuth) {
      token = await this.refreshTokenIfNeeded();
      // If token is missing, handle auth failure and return error
      if (!token) {
        this.handleAuthFailure();
        // Return a rejected promise with a clear error
        return Promise.reject(new Error("Auth token not found"));
      }
    }

    const config = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...options.headers
      },
      ...options
    };

    if (requireAuth && token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      let response = await fetch(`${this.baseUrl}${endpoint}`, config);

      if (response.status === 401 && token) {
        console.log("Token expired, attempting refresh...");
        token = await this.refreshTokenIfNeeded(true);

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          response = await fetch(`${this.baseUrl}${endpoint}`, config);
        } else {
          this.handleAuthFailure();
          throw new Error("Authentication failed");
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      return response;
    } catch (error) {
      console.error("API Request failed:", error);
      throw error;
    }
  }

  /**
   * Refresh token if expired or forced.
   * @param {boolean} [forceRefresh=false] - Force refresh token
   * @return {Promise<string|null>} New access token or null
   */
  async refreshTokenIfNeeded(forceRefresh = false) {
    const { accessToken, refreshToken } = authUtils.getTokens();

    if (!forceRefresh && authUtils.isAuthenticated()) {
      return accessToken;
    }

    if (refreshToken) {
      try {
        const response = await fetch(`${this.baseUrl}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken })
        });

        if (response.ok) {
          const responseData = await response.json();
          const data = responseData.data;
          authUtils.updateTokens(data.accessToken, data.refreshToken);
          return data.accessToken;
        }
        console.warn("Token refresh failed");
        this.handleAuthFailure();
        return null;
      } catch (error) {
        console.error("Token refresh error:", error);
        this.handleAuthFailure();
        return null;
      }
    }

    return accessToken;
  }

  /**
   * Handle authentication failures (redirects to login).
   */
  handleAuthFailure() {
    authUtils.clearStoredTokens();
  }

  /**
   * Send GET request.
   * @param {string} endpoint - API endpoint
   * @param {object} [options={}] - Request options
   * @param {boolean} requireAuth - is auth token required
   * @return {Promise<any>} Response JSON
   */
  async get(endpoint, options = {}, requireAuth = true) {
    const response = await this.request(
      endpoint,
      {
        ...options,
        method: "GET"
      },
      requireAuth
    );
    return this.handleResponse(response);
  }

  /**
   * Send POST request.
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request body
   * @param {object} [options={}] - Request options
   * @param {boolean} requireAuth - is auth token required
   * @return {Promise<any>} Response JSON
   */
  async post(endpoint, data, options = {}, requireAuth = true) {
    const response = await this.request(
      endpoint,
      {
        ...options,
        method: "POST",
        body: JSON.stringify(data)
      },
      requireAuth
    );
    return this.handleResponse(response);
  }

  /**
   * Send PUT request.
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request body
   * @param {object} [options={}] - Request options
   * @param {boolean} requireAuth - is auth token required
   * @return {Promise<any>} Response JSON
   */
  async put(endpoint, data, options = {}, requireAuth = true) {
    const response = await this.request(
      endpoint,
      {
        ...options,
        method: "PUT",
        body: JSON.stringify(data)
      },
      requireAuth
    );
    return this.handleResponse(response);
  }

  /**
   * Send DELETE request.
   * @param {string} endpoint - API endpoint
   * @param {object} [options={}] - Request options
   * @param {boolean} requireAuth - is auth token required
   * @return {Promise<any>} Response JSON
   */
  async delete(endpoint, options = {}, requireAuth = true) {
    const response = await this.request(
      endpoint,
      {
        ...options,
        method: "DELETE"
      },
      requireAuth
    );
    return this.handleResponse(response);
  }

  /**
   * Send PATCH request.
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request body
   * @param {object} [options={}] - Request options
   * @param {boolean} requireAuth - is auth token required
   * @return {Promise<any>} Response JSON
   */
  async patch(endpoint, data, options = {}, requireAuth = true) {
    const response = await this.request(
      endpoint,
      {
        ...options,
        method: "PATCH",
        body: JSON.stringify(data)
      },
      requireAuth
    );
    return this.handleResponse(response);
  }
}

/** Singleton instance of APIClient */
export const apiClient = new APIClient();
export { APIClient };
